import React, { useEffect, useState, useCallback, useRef } from "react";
import { FiUploadCloud, FiX, FiImage, FiLoader } from "react-icons/fi";
import { API_BASE } from "../../config";
import Loader from "./Loader";
import { compressImage } from "../../utils/compressImage";

const EMPTY = {
  name: "",
  category: "Export",
  description: "",
  hsnCode: "",
  varieties: "",
  importCategory: "",
  countryOfOrigin: "",
  packaging: "",
  shelfLife: "",
  mandatoryCertifications: "",
  indianCompliance: "",
  freightix1012Role: "",
  image: "",
};

const ITEMS_PER_PAGE = 5;

function ProductManager({ token }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [preparing, setPreparing] = useState(false);

  // File picked but NOT yet uploaded — stays local until "Create"/"Update"
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(""); // local blob URL or existing remote URL
  const fileInputRef = useRef(null);
  const blobUrlRef = useRef(null);

  const authHeader = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const revokeBlob = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(() => () => revokeBlob(), []); // cleanup on unmount

  const fetchProducts = useCallback(async (targetPage = page) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/products/admin/all?page=${targetPage}&limit=${ITEMS_PER_PAGE}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to load products");
      const json = await res.json();
      setProducts(json.data || []);
      setTotalPages(json.totalPages || 1);
      setTotalProducts(json.total || 0);
    } catch (err) {
      setMsg(err.message || "Failed to load products");
    }
    setLoading(false);
  }, [token, page]);

  useEffect(() => {
    fetchProducts(page);
  }, [page, fetchProducts]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // ── Step 1: pick file → validate, compress, show INSTANT local preview (no upload yet) ──
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const maxMB = isVideo ? 50 : 10;

    if (!isImage && !isVideo) {
      setMsg("Only images (jpg, png, webp, gif) or videos (mp4, webm) are allowed");
      return;
    }
    if (file.size > maxMB * 1024 * 1024) {
      setMsg(`File too large — max ${maxMB}MB`);
      return;
    }

    setPreparing(true);
    setMsg("");
    let fileToStore = file;
    if (isImage) {
      try {
        fileToStore = await compressImage(file);
      } catch {
        fileToStore = file; // compression failed — use original
      }
    }
    setPreparing(false);

    revokeBlob();
    const blobUrl = URL.createObjectURL(fileToStore);
    blobUrlRef.current = blobUrl;
    setSelectedFile(fileToStore);
    setPreviewSrc(blobUrl);
  };

  // ── Step 2: called on submit — upload the picked file to S3, return its public URL ──
  const uploadToS3 = async (file) => {
    const presignRes = await fetch(`${API_BASE}/upload/presign`, {
      method: "POST",
      headers: authHeader,
      body: JSON.stringify({ fileName: file.name, fileType: file.type }),
    });
    if (!presignRes.ok) {
      const data = await presignRes.json().catch(() => ({}));
      throw new Error(data.error || "Failed to get upload URL");
    }
    const { uploadUrl, publicUrl } = await presignRes.json();

    const uploadRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!uploadRes.ok) throw new Error("Failed to upload image to S3");

    return publicUrl;
  };

  const deleteOldS3Image = async (url) => {
    if (!url || !url.includes(".s3.")) return;
    try {
      const key = url.split(".amazonaws.com/")[1];
      if (key) {
        await fetch(`${API_BASE}/upload/file?key=${encodeURIComponent(key)}`, {
          method: "DELETE",
          headers: authHeader,
        });
      }
    } catch {
      // best effort — product save continues even if S3 cleanup fails
    }
  };

  // ── Step 3: submit → upload file (if any) → save product ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || preparing) return; // hard double-submit guard
    setMsg("");
    setSaving(true);
    try {
      let imageUrl = form.image;

      if (selectedFile) {
        setMsg("Uploading image…");
        imageUrl = await uploadToS3(selectedFile);

        // Editing and replaced the old image → remove the old S3 file
        if (editingId && form.image && form.image !== imageUrl) {
          await deleteOldS3Image(form.image);
        }
      }

      const url = editingId ? `${API_BASE}/products/${editingId}` : `${API_BASE}/products`;
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: authHeader,
        body: JSON.stringify({ ...form, image: imageUrl }),
      });
      if (!res.ok) throw new Error("Failed to save product");

      setMsg(editingId ? "Product updated" : "Product created");
      setForm(EMPTY);
      setEditingId(null);
      setSelectedFile(null);
      revokeBlob();
      setPreviewSrc("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (!editingId) {
        setPage(1);
        fetchProducts(1);
      } else {
        fetchProducts(page);
      }
    } catch (err) {
      setMsg(err.message || "Failed to save product");
    }
    setSaving(false);
  };

  const handleEdit = (p) => {
    setEditingId(p._id);
    setForm({
      name: p.name || "",
      category: p.category || "Export",
      description: p.description || "",
      hsnCode: p.hsnCode || "",
      varieties: p.varieties || "",
      importCategory: p.importCategory || "",
      countryOfOrigin: p.countryOfOrigin || "",
      packaging: p.packaging || "",
      shelfLife: p.shelfLife || "",
      mandatoryCertifications: p.mandatoryCertifications || "",
      indianCompliance: p.indianCompliance || "",
      freightix1012Role: p.freightix1012Role || "",
      image: p.image || "",
    });
    setSelectedFile(null);
    revokeBlob();
    setPreviewSrc(p.image || "");
    if (fileInputRef.current) fileInputRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearImage = () => {
    setSelectedFile(null);
    revokeBlob();
    setPreviewSrc("");
    setForm((f) => ({ ...f, image: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await deleteOldS3Image(product.image); // best-effort S3 cleanup
      const res = await fetch(`${API_BASE}/products/${product._id}`, { method: "DELETE", headers: authHeader });
      if (!res.ok) throw new Error("Failed to delete product");
      setMsg("Product deleted");
      const nextCount = products.length - 1;
      const targetPage = nextCount === 0 && page > 1 ? page - 1 : page;
      if (targetPage !== page) {
        setPage(targetPage);
      } else {
        fetchProducts(targetPage);
      }
    } catch (err) {
      setMsg(err.message || "Failed to delete product");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(EMPTY);
    setSelectedFile(null);
    revokeBlob();
    setPreviewSrc("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isVideo = previewSrc && /\.(mp4|webm|mov)(\?|$)/i.test(previewSrc);

  return (
    <div className="pm">
      <form className="pm-form" onSubmit={handleSubmit}>
        <h2>{editingId ? "Edit Product" : "Add Product"}</h2>
        <div className="pm-row">
          <input name="name" placeholder="Product Name *" value={form.name} onChange={handleChange} required />
          <select name="category" value={form.category} onChange={handleChange}>
            <option value="Export">Export</option>
            <option value="Import">Import</option>
          </select>
        </div>
        <textarea name="description" placeholder="Description *" value={form.description} onChange={handleChange} required rows="3" />

        {/* Product Specifications (Added after Description) */}
        <div className="pm-specs-section">
          <h3 className="pm-specs-title">Product Specifications</h3>
          <div className="pm-specs-grid">
            <div className="pm-field">
              <label>HSN Code</label>
              <input
                name="hsnCode"
                placeholder="e.g. 1006.30.20"
                value={form.hsnCode}
                onChange={handleChange}
              />
            </div>
            <div className="pm-field">
              <label>Varieties</label>
              <input
                name="varieties"
                placeholder="e.g. 1121 XXL, Pusa, Sugandha"
                value={form.varieties}
                onChange={handleChange}
              />
            </div>
            <div className="pm-field">
              <label>Import Category</label>
              <input
                name="importCategory"
                placeholder="e.g. Agro Commodities"
                value={form.importCategory}
                onChange={handleChange}
              />
            </div>
            <div className="pm-field">
              <label>Country of Origin</label>
              <input
                name="countryOfOrigin"
                placeholder="e.g. India"
                value={form.countryOfOrigin}
                onChange={handleChange}
              />
            </div>
            <div className="pm-field">
              <label>Packaging</label>
              <input
                name="packaging"
                placeholder="e.g. 25kg / 50kg Bags"
                value={form.packaging}
                onChange={handleChange}
              />
            </div>
            <div className="pm-field">
              <label>Shelf Life</label>
              <input
                name="shelfLife"
                placeholder="e.g. 24 Months"
                value={form.shelfLife}
                onChange={handleChange}
              />
            </div>
            <div className="pm-field">
              <label>Mandatory Certifications</label>
              <input
                name="mandatoryCertifications"
                placeholder="e.g. FSSAI, APEDA, Phytosanitary"
                value={form.mandatoryCertifications}
                onChange={handleChange}
              />
            </div>
            <div className="pm-field">
              <label>Indian Compliance</label>
              <input
                name="indianCompliance"
                placeholder="e.g. DGFT, Customs Tariff Act"
                value={form.indianCompliance}
                onChange={handleChange}
              />
            </div>
            <div className="pm-field pm-field-full">
              <label>Freightix1012 Role</label>
              <input
                name="freightix1012Role"
                placeholder="e.g. Quality Inspection, Customs Clearance, Freight Forwarding"
                value={form.freightix1012Role}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Image / video picker — preview is local, upload happens on submit */}
        <div className="pm-upload">
          {preparing && <p className="pm-upload-note">Optimizing image…</p>}
          {previewSrc ? (
            <div className="pm-upload-preview">
              {isVideo ? (
                <video src={previewSrc} controls />
              ) : (
                <img src={previewSrc} alt="Product preview" />
              )}
              <button type="button" className="pm-upload-remove" onClick={clearImage} title="Remove image">
                <FiX />
              </button>
              <label className="pm-upload-replace">
                <FiUploadCloud /> Replace file
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                  onChange={handleFileChange}
                  hidden
                />
              </label>
            </div>
          ) : (
            <label className="pm-upload-dropzone" title="Choose image or video">
              <FiUploadCloud />
              <span>Click to upload image or video</span>
              <small>JPG, PNG, WEBP, GIF up to 10MB · MP4, WEBM up to 50MB</small>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                onChange={handleFileChange}
                hidden
              />
            </label>
          )}
        </div>

        <div className="pm-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={saving || preparing}
            aria-busy={saving}
          >
            {saving ? (
              <>
                <FiLoader className="btn-spin" /> Saving…
              </>
            ) : editingId ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
          {editingId && (
            <button type="button" className="btn-secondary" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          )}
        </div>
        {msg && <p className="pm-msg">{msg}</p>}
      </form>

      <div className="pm-list">
        <div className="pm-list-header">
          <h3>Products Catalog ({totalProducts})</h3>
          <span className="pm-list-sub">Showing up to {ITEMS_PER_PAGE} per page</span>
        </div>

        {loading && <Loader text="Loading products..." />}

        {!loading && products.length === 0 && (
          <p className="pm-empty">No products found on this page.</p>
        )}

        {products.map((p) => (
          <div key={p._id} className="pm-item">
            <div className="pm-item-head">
              {p.image ? (
                /\.(mp4|webm|mov)(\?|$)/i.test(p.image) ? (
                  <video src={p.image} className="pm-thumb" muted />
                ) : (
                  <img src={p.image} alt={p.name} className="pm-thumb" onError={(e) => { e.target.style.display = "none"; }} />
                )
              ) : (
                <span className="pm-thumb pm-thumb-empty"><FiImage /></span>
              )}
              <div>
                <strong>{p.name}</strong>
                <span className="pm-cat">{p.category}</span>
                {!p.active && <span className="pm-inactive">inactive</span>}
              </div>
            </div>
            <p>{p.description}</p>
            {(p.hsnCode || p.varieties || p.countryOfOrigin || p.packaging || p.shelfLife || p.importCategory || p.mandatoryCertifications || p.indianCompliance || p.freightix1012Role) && (
              <div className="pm-item-specs">
                {p.hsnCode && <span className="pm-spec-pill"><strong>HSN:</strong> {p.hsnCode}</span>}
                {p.countryOfOrigin && <span className="pm-spec-pill"><strong>Origin:</strong> {p.countryOfOrigin}</span>}
                {p.varieties && <span className="pm-spec-pill"><strong>Varieties:</strong> {p.varieties}</span>}
                {p.packaging && <span className="pm-spec-pill"><strong>Pkg:</strong> {p.packaging}</span>}
                {p.shelfLife && <span className="pm-spec-pill"><strong>Shelf:</strong> {p.shelfLife}</span>}
                {p.importCategory && <span className="pm-spec-pill"><strong>Cat:</strong> {p.importCategory}</span>}
                {p.mandatoryCertifications && <span className="pm-spec-pill"><strong>Certs:</strong> {p.mandatoryCertifications}</span>}
                {p.indianCompliance && <span className="pm-spec-pill"><strong>Compliance:</strong> {p.indianCompliance}</span>}
                {p.freightix1012Role && <span className="pm-spec-pill"><strong>Freightix1012 Role:</strong> {p.freightix1012Role}</span>}
              </div>
            )}
            <div className="pm-item-actions">
              <button className="btn-secondary" onClick={() => handleEdit(p)}>Edit</button>
              <button className="btn-danger" onClick={() => handleDelete(p)}>Delete</button>
            </div>
          </div>
        ))}

        {totalPages > 1 && (
          <div className="pm-pagination">
            <button
              type="button"
              className="btn-secondary"
              disabled={page === 1 || loading}
              onClick={() => {
                setPage((prev) => Math.max(1, prev - 1));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Prev
            </button>
            <span className="pm-pagination-info">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={page === totalPages || loading}
              onClick={() => {
                setPage((prev) => Math.min(totalPages, prev + 1));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductManager;
